import {
  Box,
  Button,
  Icon,
  Modal,
  ModalActions,
  ModalBody,
  ModalCloseButton,
  ModalCloseTrigger,
  ModalContent,
  ModalTitle,
  Text
} from '@qcx/ui';
import {
  faArrowLeft,
  faEllipsisH,
  faThumbsUp,
  faTrash
} from '@fortawesome/pro-regular-svg-icons';
import { shallow } from '@qcx/store';
import { useNewsStore } from '../../../news.store';
import { useState } from 'react';
import { z } from 'zod';

export const CommentActions = ({
  commentId,
  reactionsCount: initialReactionsCount,
  authorId
}: {
  commentId: number;
  reactionsCount: number;
  authorId: number;
}) => {
  const userId = useNewsStore(s => s.userInfo?.id, shallow);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const comment = useNewsStore(
    s => s.comments.commentaries.find(c => c.id === commentId),
    shallow
  );
  const commentaries = useNewsStore(s => s.comments.commentaries, shallow);
  const setCommentaries = useNewsStore(s => s.setCommentaries, shallow);
  const [reportStep, setReportStep] = useState<1 | 2 | 3>(1);

  const hasUserReaction = userId
    ? comment?.articleCommentaryReaction.includes(userId)
    : false;

  const [userLike, setUserLike] = useState(hasUserReaction);
  const [localReactionsCount, setLocalReactionsCount] = useState(
    initialReactionsCount
  );
  const [reportReason, setReportReason] = useState<{
    reason: string;
    description: string;
  }>({
    reason: '',
    description: ''
  });

  const reportReasonSchema = z.object({
    reason: z.string().min(1),
    description: z.string().min(30)
  });

  const validateReportReason = () => {
    const result = reportReasonSchema.safeParse(reportReason);
    return result.success;
  };

  const isValid = validateReportReason();

  const isOwner = authorId === userId;

  const handleToggleLike = async () => {
    setLoading(true);

    try {
      const response = await fetch(`/api/comments/${commentId}/reaction`, {
        method: userLike ? 'DELETE' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      setUserLike(!userLike);
      setLocalReactionsCount(prev => (userLike ? prev - 1 : prev + 1));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setDeleteModalOpen(false);

    if (!isOwner) {
      return;
    }

    try {
      const response = await fetch(`/api/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return;
      }

      const updatedCommentaries = commentaries.filter(
        comment => comment.id !== commentId
      );
      setCommentaries(updatedCommentaries);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleCloseWithAction = () => {
    setDeleteModalOpen(false);
    setReportStep(1);
    setReportReason({
      reason: '',
      description: ''
    });
  };

  const handleSubmitReport = async () => {
    if (!validateReportReason()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/comments/report', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resourceId: commentId,
          text: `${reportReason.reason} - ${reportReason.description}`
        })
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      handleCloseWithAction();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        css={{
          display: 'flex',
          gap: '$2',
          alignItems: 'center'
        }}
      >
        <Box
          as="button"
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            border: userLike ? '1px solid #FE6112' : '1px solid #CBD5E1',
            borderRadius: '8px',
            p: '6px 8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            '&:hover': {
              bgColor: loading ? 'transparent' : '#F1F5F9'
            },
            '&:hover .like-count-box': {
              bgColor: loading ? '#F1F5F9' : '#FFFFFF'
            }
          }}
          disabled={loading}
          onClick={loading ? undefined : handleToggleLike}
        >
          <Icon
            css={{
              minMaxSize: '16px',
              color: userLike ? '#FE6112' : '#64748B',
              display: 'flex',
              alignItems: 'center'
            }}
            icon={faThumbsUp}
            size="md"
          />
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              bgColor: '#F1F5F9',
              borderRadius: '4px'
            }}
            className="like-count-box"
          >
            <Text
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minMaxSize: '16px',
                lineHeight: '16px',
                fontSize: '$xs',
                fontWeight: '$medium',
                color: '#334155'
              }}
            >
              {localReactionsCount}
            </Text>
          </Box>
        </Box>
        <Modal
          open={deleteModalOpen}
          onOpenChange={open => {
            setDeleteModalOpen(open);

            if (!open) {
              setReportStep(1);
              setReportReason({
                reason: '',
                description: ''
              });
            }
          }}
        >
          <Box
            as="button"
            css={{
              w: 'fit-content',
              display: 'flex',
              alignItems: 'center',
              p: '6px 8px',
              gap: '6px',
              border: '1px solid #CBD5E1',
              borderRadius: '8px',
              cursor: 'pointer',
              '&:hover': {
                bgColor: '#F1F5F9'
              },
              '&:hover .like-count-box': {
                bgColor: '#FFFFFF'
              }
            }}
            onClick={() => setDeleteModalOpen(true)}
          >
            <Icon css={{ minMaxSize: '16px' }} icon={faEllipsisH} size="md" />
          </Box>
          {isOwner ? (
            <ModalContent css={{ maxW: 400, p: '$2 $4 $6 $4' }}>
              <ModalBody>
                <ModalTitle
                  css={{
                    mb: '$6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  Deseja excluir seu comentário?
                  <ModalCloseButton
                    css={{ position: 'relative', top: '$0', right: '$0' }}
                  />
                </ModalTitle>
              </ModalBody>
              <ModalActions
                css={{ flexDirection: 'column', gap: '$2', mt: '$0' }}
              >
                <Button
                  css={{
                    color: '#DC2626',
                    fontWeight: '$medium',
                    justifyContent: 'flex-start',
                    border: '1px solid #E5E7EB',
                    '&:hover': {
                      color: '#d90f0f !important'
                    }
                  }}
                  fullWidth
                  onClick={handleDelete}
                >
                  <Box
                    css={{ display: 'flex', alignItems: 'center', gap: '$2' }}
                  >
                    <Icon icon={faTrash} />
                    <Text>Sim, quero excluir</Text>
                  </Box>
                </Button>
                <ModalCloseTrigger asChild>
                  <Button
                    css={{
                      justifyContent: 'flex-start',
                      border: '1px solid #E5E7EB'
                    }}
                    fullWidth
                  >
                    <Box
                      css={{ display: 'flex', alignItems: 'center', gap: '$2' }}
                    >
                      <Icon icon={faArrowLeft} />
                      <Text>Cancelar</Text>
                    </Box>
                  </Button>
                </ModalCloseTrigger>
              </ModalActions>
            </ModalContent>
          ) : (
            <ModalContent css={{ maxW: 400, p: '$2 $4 $6 $4' }}>
              {reportStep === 1 && (
                <>
                  <ModalBody>
                    <ModalTitle
                      css={{
                        mb: '$6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      Deseja denunciar este comentário?
                      <ModalCloseButton
                        css={{ position: 'relative', top: '$0', right: '$0' }}
                        onPress={() => handleCloseWithAction()}
                      />
                    </ModalTitle>
                  </ModalBody>
                  <ModalActions
                    css={{ flexDirection: 'column', gap: '$2', mt: '$0' }}
                  >
                    <Button
                      css={{
                        color: '#DC2626',
                        fontWeight: '$medium',
                        justifyContent: 'flex-start',
                        border: '1px solid #E5E7EB',
                        '&:hover': {
                          color: '#d90f0f !important'
                        }
                      }}
                      fullWidth
                      onClick={() => setReportStep(2)}
                    >
                      <Box
                        css={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '$2'
                        }}
                      >
                        <Icon icon={faTrash} />
                        <Text>Sim, quero denunciar</Text>
                      </Box>
                    </Button>
                    <ModalCloseTrigger asChild>
                      <Button
                        css={{
                          justifyContent: 'flex-start',
                          border: '1px solid #E5E7EB'
                        }}
                        fullWidth
                      >
                        <Box
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '$2'
                          }}
                        >
                          <Icon icon={faArrowLeft} />
                          <Text>Cancelar</Text>
                        </Box>
                      </Button>
                    </ModalCloseTrigger>
                  </ModalActions>
                </>
              )}
              {reportStep === 2 && (
                <>
                  <ModalBody>
                    <ModalTitle
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: '#111827',
                        fontSize: '$md',
                        fontWeight: '$medium'
                      }}
                    >
                      Qual o motivo da denúncia?
                      <ModalCloseButton
                        css={{ position: 'relative', top: '$0', right: '$0' }}
                        onPress={() => {
                          setDeleteModalOpen(false);
                          setReportStep(1);
                          setReportReason({
                            reason: '',
                            description: ''
                          });
                        }}
                      />
                    </ModalTitle>
                    <Box
                      css={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '$2',
                        mt: '$4'
                      }}
                    >
                      {[
                        'Spam',
                        'Conteúdo ofensivo ou impróprio',
                        'Informação falsa',
                        'Simplesmente não gostei'
                      ].map(value => (
                        <Button
                          key={value}
                          css={{
                            color: '#334155',
                            fontWeight: '$medium',
                            justifyContent: 'flex-start',
                            border: '1px solid #E5E7EB',
                            '&:hover': {
                              color: '#111827 !important'
                            }
                          }}
                          fullWidth
                          onClick={() => {
                            setReportReason({
                              ...reportReason,
                              reason: value
                            });
                            setReportStep(3);
                          }}
                        >
                          <Text>{value}</Text>
                        </Button>
                      ))}
                      <ModalCloseTrigger asChild>
                        <Button
                          css={{
                            justifyContent: 'flex-start',
                            border: '1px solid #E5E7EB'
                          }}
                          fullWidth
                          onClick={() => {
                            setTimeout(() => {
                              setReportStep(1);
                              setReportReason({
                                reason: '',
                                description: ''
                              });
                            }, 100);
                          }}
                        >
                          <Box
                            css={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '$2'
                            }}
                          >
                            <Icon icon={faArrowLeft} />
                            <Text>Cancelar</Text>
                          </Box>
                        </Button>
                      </ModalCloseTrigger>
                    </Box>
                  </ModalBody>
                </>
              )}

              {reportStep === 3 && (
                <>
                  <ModalBody>
                    <ModalTitle
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: '#111827',
                        fontSize: '$md',
                        fontWeight: '$medium'
                      }}
                    >
                      Descreva o motivo da denúncia
                      <ModalCloseButton
                        css={{ position: 'relative', top: '$0', right: '$0' }}
                        onPress={() => {
                          setDeleteModalOpen(false);
                          setReportStep(1);
                          setReportReason({
                            reason: '',
                            description: ''
                          });
                        }}
                      />
                    </ModalTitle>
                    <Box
                      css={{ w: '$full', d: 'flex', justifyContent: 'center' }}
                    >
                      <textarea
                        style={{
                          width: '100%',
                          height: '120px',
                          minHeight: '120px',
                          maxHeight: '120px',
                          resize: 'none',
                          padding: '8px',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          marginTop: '16px',
                          fontSize: '14px'
                        }}
                        placeholder="Escreva detalhes sobre o motivo da denúncia (mínimo 30 caracteres)"
                        value={reportReason.description}
                        onChange={e =>
                          setReportReason({
                            ...reportReason,
                            description: e.target.value
                          })
                        }
                      />
                    </Box>
                    <Box
                      css={{
                        mt: '$4',
                        d: 'flex',
                        justifyContent: 'flex-end',
                        gap: '$2'
                      }}
                    >
                      <Button
                        css={{
                          bgColor: '#FE6112',
                          color: '#FFFFFF',
                          '&:hover': {
                            bgColor: '#FE6112 !important',
                            color: '#FFFFFF !important',
                            opacity: 0.8
                          },
                          '&:disabled': {
                            bgColor: '#FE6112 !important',
                            color: '#FFFFFF !important',
                            opacity: 0.8
                          }
                        }}
                        disabled={!isValid || loading}
                        fullWidth
                        onClick={handleSubmitReport}
                      >
                        {loading ? 'Enviando...' : 'Enviar'}
                      </Button>
                    </Box>
                  </ModalBody>
                </>
              )}
            </ModalContent>
          )}
        </Modal>
      </Box>
    </Box>
  );
};
